import { jupiterFetch } from '@svs/nova-core/jupiter';

import {
  marketplaceBalanceApiResponse,
  marketplaceGroupsApiResponse,
  retailerBalanceApiResponse,
} from '../../../schema/marketplace/entry.js';
import { ApiError, type IMarketplaceGroupsService } from '../../domain/entry.js';

const getMarketplaceGroupsAsync: IMarketplaceGroupsService['getMarketplaceGroupsAsync'] = async () => {
  const marketplaceGroupsResponse = await jupiterFetch(`/marketplace/3/groups`, {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  });

  if (!marketplaceGroupsResponse.ok) {
    throw new ApiError('Failed to fetch marketplace groups', marketplaceGroupsResponse.status);
  }

  const json = (await marketplaceGroupsResponse.json()) as unknown;

  const response = marketplaceGroupsApiResponse.parse(json);

  if ('error' in response) {
    throw ApiError.fromBackendError(response.error);
  }

  return response.data;
};

const getMarketplaceBalanceAsync: IMarketplaceGroupsService['getMarketplaceBalanceAsync'] = async ({
  product,
  drawNumber,
}) => {
  const marketplaceResponse = await jupiterFetch(
    `/marketplace/1/activateddraw/balance?product=${product}&drawNumber=${drawNumber}`,
    {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      credentials: 'include',
      cache: 'no-store',
    }
  );

  if (!marketplaceResponse.ok) {
    throw new ApiError('Failed to fetch marketplace balance', marketplaceResponse.status);
  }

  const json = (await marketplaceResponse.json()) as unknown;

  const response = marketplaceBalanceApiResponse.parse(json);

  if ('error' in response) {
    throw ApiError.fromBackendError(response.error);
  }

  return response.data;
};

const getRetailerBalanceAsync: IMarketplaceGroupsService['getRetailerBalanceAsync'] = async ({ activatedDrawId }) => {
  const retailerResponse = await jupiterFetch(`/retailer/1/marketplace/activateddraws/${activatedDrawId}`, {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  });

  if (!retailerResponse.ok) {
    throw new ApiError('Failed to fetch retailer balance', retailerResponse.status);
  }

  const json = (await retailerResponse.json()) as unknown;

  const response = retailerBalanceApiResponse.parse(json);

  if ('error' in response) {
    throw ApiError.fromBackendError(response.error);
  }

  return response.data;
};

/**
 * Creates a marketplace groups service instance for client.
 *
 * @returns The marketplace groups service implementation
 */
export function marketplaceGroupsServiceClient(): IMarketplaceGroupsService {
  return {
    getMarketplaceGroupsAsync,
    getMarketplaceBalanceAsync,
    getRetailerBalanceAsync,
  };
}
